import moment from "moment";

import { ContactFileModel } from "../../factories/contacts/models/contact-file.model";
import { FileItem } from "../files/files.types";
import { ClientPortalPerson, PortalFileFields } from "./clientPortal.types";

export type PortalFileState = "fromClient" | "shared" | "firm";
export type PortalFileFilter = "all" | "fromClient" | "shared";

/** Where a client file stands in the portal. */
export const portalFileState = (file: PortalFileFields): PortalFileState =>
  file.portalUserId ? "fromClient" : file.sharedWithClient ? "shared" : "firm";

/** A client upload nobody at the firm has looked at yet. */
export const isNewClientUpload = (file: PortalFileFields): boolean =>
  !!file.portalUserId && !file.seenByFirmAt;

export const filterPortalFiles = <T extends PortalFileFields>(
  files: T[],
  filter: PortalFileFilter,
): T[] =>
  filter === "all" ? files : files.filter((f) => portalFileState(f) === filter);

export const countPortalFiles = (files: PortalFileFields[]) => ({
  all: files.length,
  fromClient: files.filter((f) => portalFileState(f) === "fromClient").length,
  shared: files.filter((f) => portalFileState(f) === "shared").length,
});

export const personName = (
  p: Pick<ClientPortalPerson, "firstName" | "lastName" | "email">,
): string =>
  [p.firstName, p.lastName].filter((x) => x && x.trim()).join(" ") || p.email;

export const personDetail = (p: ClientPortalPerson): string => {
  if (p.status === "invited" && p.invitedAt)
    return `Invited ${moment(p.invitedAt).format("MMM D")}`;
  if (p.status === "active" && p.lastSignInAt)
    return `Last signed in ${moment(p.lastSignInAt).format("MMM D")}`;
  return p.email;
};

export const statusLabel: Record<ClientPortalPerson["status"], string> = {
  active: "Active",
  invited: "Invited",
  revoked: "Access removed",
  none: "No access",
};

/**
 * People shown on the client's portal card (QA batch 15): those with access or
 * an invitation first, then people whose access was removed ("Access
 * removed"), so the card doesn't say "Nobody has access yet" after a removal.
 */
export const portalCardPeople = (
  people: ClientPortalPerson[],
): ClientPortalPerson[] => [
  ...people.filter((p) => p.status === "active" || p.status === "invited"),
  ...people.filter((p) => p.status === "revoked" && p.accessId),
];

/** The portal card's sentence when nobody is listed. */
export const portalEmptyText = (archived?: boolean): string =>
  archived
    ? "This client is archived. Unarchive it to invite people to the portal."
    : "Nobody has access yet. Invite this client’s contacts so they can upload documents and see what you share.";

/** "Portal active" when someone can sign in, "Invited" when only invitations are out. */
export const portalSummary = (
  people: ClientPortalPerson[],
): "active" | "invited" | "none" =>
  people.some((p) => p.status === "active")
    ? "active"
    : people.some((p) => p.status === "invited")
    ? "invited"
    : "none";

/** Splits "Sara Ruiz" into first/last name for a person typed in by hand. */
export const splitName = (
  name: string,
): { firstName?: string; lastName?: string } => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return {};
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" ") || undefined,
  };
};

/** "Northwind Traders’" / "Maria’s". */
export const possessive = (name: string): string =>
  /s$/i.test(name.trim()) ? `${name.trim()}’` : `${name.trim()}’s`;

export const isEmailAddress = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const DEFAULT_INVITE_MESSAGE = (clientName: string) =>
  `Hi, we’ve set up your secure client portal. You can upload documents for ${clientName} there and see everything we share with you.`;

/**
 * A file in the Client portal widget's "Files" tab (Roadmap 1879): a client
 * file the client sees in the portal. `sharedFolder` = it is there only
 * because its folder is shared (the per-file switch can't take it out).
 */
export type PortalTabFile = PortalFileFields & {
  id: string;
  name: string;
  addedAt: string | null;
  sharedFolder: string | null;
  item: FileItem;
};

/**
 * The client's files that are in the portal (api/files/clients: client uploads,
 * files shared one by one, files in a shared folder), newest first, in the
 * shape the portal pieces (filters, tags, share switch) use.
 */
export const portalTabFiles = (files: FileItem[]): PortalTabFile[] =>
  files
    .filter((f) => f.kind === "client" && !f.deletedAt)
    .filter((f) => f.fromClient || f.clientCanSee)
    .map((f) => ({
      id: f.id,
      name: f.name,
      addedAt: f.addedAt,
      sharedWithClient: !f.fromClient,
      portalUserId: f.fromClient ? "client" : null,
      portalUploaderName: f.fromClient ? f.addedBy : null,
      seenByFirmAt: f.fromClient && f.isNew ? null : f.addedAt ?? "seen",
      notes: f.note,
      sharedFolder:
        !f.fromClient && !f.sharedDirectly
          ? f.folderPath || "a shared folder"
          : null,
      item: f,
    }))
    .sort(
      (a, b) =>
        new Date(b.addedAt ?? 0).getTime() - new Date(a.addedAt ?? 0).getTime(),
    );

/** Client files the firm could still share ("Share with client" window list). */
export const portalShareCandidates = (files: FileItem[]): ContactFileModel[] =>
  files
    .filter(
      (f) =>
        f.kind === "client" && !f.deletedAt && !f.fromClient && !f.clientCanSee,
    )
    .map((f) => ({
      id: f.id,
      originalFileName: f.name,
      createdDate: f.addedAt ?? "",
    }));
